import axios from "axios";
import i18n from "../i18n";

const PYTHON_API_URL = "https://api.ai.raborisk.ai"
const getLang = () => i18n.language || "tr";
/**
 * Function to call the backend OpenAI API dynamically based on the use case
 * @param {string} useCase - The use case to determine the API behavior (e.g., "suggestControl", "generalChat").
 * @param {object} params - Parameters required for the selected use case. Example: { title, details } for suggestControl or { query } for generalChat.
 * @returns {Promise<string>} - Returns the AI-generated response.
 */
const callOpenAIAPI = async (title, description, threats = []) => {
  try {
    const threatTexts = threats.map(t => t.text ?? t);
    //console.log("threats", threatTexts)
    const { data } = await axios.post(
      `${PYTHON_API_URL}/controls`,
      { title, description, threats: threatTexts, language: getLang() },
      { headers: { "Content-Type": "application/json" } }
    );
    return data;
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};

// Non-IT departments: let the AI invent its own controls instead of matching
// against the fixed (IT/security-flavored) control library. Subject to change later.
export const callOpenAIAPIControlsFreeform = async (title, description, threats = [], departmentName = "", departmentDefinition = "") => {
  try {
    const threatTexts = threats.map(t => t.text ?? t);
    const { data } = await axios.post(
      `${PYTHON_API_URL}/controls_freeform`,
      {
        title,
        description,
        threats: threatTexts,
        department_name: departmentName,
        department_definition: departmentDefinition,
        language: getLang(),
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return data;
  } catch (err) {
    console.error("Error calling OpenAI API (controls freeform):", err);
    throw new Error("Failed to fetch AI response.");
  }
};


export const callOpenAIAPIChecklist = async (title, description) => {
  try {
    const { data } = await axios.post(
      `${PYTHON_API_URL}/control_checklist`,
      { title, description, language: getLang() },
      { headers: { "Content-Type": "application/json" } }
    );

    // `data` itself is the answer
    return data;                         // no `.reply`
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};

export const callOpenAIAPIPolicyHealthWithFile = async (
  policyDescription,
  relatedCompliance = [],
  file // File | null
) => {
  try {
    const formData = new FormData();
    formData.append("policyDescription", policyDescription);
    formData.append("language", getLang());

    if (relatedCompliance?.length) {
      formData.append("relatedCompliance", JSON.stringify(relatedCompliance));
    }

    if (file) {
      formData.append("file", file);
    }

    const { data } = await axios.post(
      `${PYTHON_API_URL}/policy_health_file`,
      formData,
      {
        headers: {
          // IMPORTANT: let axios set boundary automatically
          "Content-Type": "multipart/form-data"
        }
      }
    );

    // data => { "healthScore": 0-100 }
    return data;
  } catch (err) {
    console.error("Error calling Policy Health API:", err);
    throw new Error("Failed to fetch policy health score.");
  }
};

export const callOpenAIAPIPolicyHealthWithFileV2 = async (
  policyDescription,
  relatedCompliance = [],
  file // File | null
) => {
  try {
    const formData = new FormData();
    formData.append("policyDescription", policyDescription);
    formData.append("language", getLang());

    if (relatedCompliance?.length) {
      formData.append("relatedCompliance", JSON.stringify(relatedCompliance));
    }

    if (file) {
      formData.append("file", file);
    }

    const { data } = await axios.post(
      `${PYTHON_API_URL}/policy_health_file_v2`,
      formData,
      {
        headers: {
          // Let axios handle boundary
          "Content-Type": "multipart/form-data"
        }
      }
    );

    /*
      data =>
      {
        "healthScore": 0-100,
        "Compliance": 0-100,
        "Completeness": 0-100,
        "Clarity": 0-100,
        "AIRecommendations": [...]
      }
    */

    return data;
  } catch (err) {
    console.error("Error calling Policy Health V2 API:", err);
    throw new Error("Failed to fetch policy health score (gold standard).");
  }
};



const THREAT_CATALOG = [
  { code: "TP1",  category: "Physical damage",                  text: "Fire" },
  { code: "TP2",  category: "Physical damage",                  text: "Water damage" },
  { code: "TP3",  category: "Physical damage",                  text: "Pollution" },
  { code: "TP4",  category: "Physical damage",                  text: "Major accident" },
  { code: "TP5",  category: "Physical damage",                  text: "Destruction of equipment or media" },
  { code: "TP6",  category: "Physical damage",                  text: "Dust, corrosion, freezing" },
  { code: "TN1",  category: "Natural events",                   text: "Climatic phenomenon" },
  { code: "TN2",  category: "Natural events",                   text: "Seismic phenomenon" },
  { code: "TN3",  category: "Natural events",                   text: "Volcanic phenomenon" },
  { code: "TN4",  category: "Natural events",                   text: "Meteorological phenomenon" },
  { code: "TN5",  category: "Natural events",                   text: "Flood" },
  { code: "TN6",  category: "Natural events",                   text: "Pandemic/epidemic phenomenon" },
  { code: "TI01", category: "Infrastructure failures",           text: "Failure of supply system" },
  { code: "TI02", category: "Infrastructure failures",           text: "Failure of cooling or ventilation system" },
  { code: "TI03", category: "Infrastructure failures",           text: "Loss of power supply" },
  { code: "TI04", category: "Infrastructure failures",           text: "Failure of a telecommunications network" },
  { code: "TI05", category: "Infrastructure failures",           text: "Failure of telecommunication equipment" },
  { code: "TI06", category: "Infrastructure failures",           text: "Electromagnetic radiation" },
  { code: "TI07", category: "Infrastructure failures",           text: "Thermal radiation" },
  { code: "TI08", category: "Infrastructure failures",           text: "Electromagnetic pulses" },
  { code: "TT01", category: "Technical failures",               text: "Failure of device or system" },
  { code: "TT02", category: "Technical failures",               text: "Saturation of the information system" },
  { code: "TT03", category: "Technical failures",               text: "Violation of information system maintainability" },
  { code: "TH01", category: "Human actions",                    text: "Terror, attack, sabotage" },
  { code: "TH02", category: "Human actions",                    text: "Social engineering" },
  { code: "TH03", category: "Human actions",                    text: "Interception of radiation of a device" },
  { code: "TH04", category: "Human actions",                    text: "Remote spying" },
  { code: "TH05", category: "Human actions",                    text: "Eavesdropping" },
  { code: "TH06", category: "Human actions",                    text: "Theft of media or document" },
  { code: "TH07", category: "Human actions",                    text: "Theft of equipment" },
  { code: "TH08", category: "Human actions",                    text: "Theft of digital identity or credentials" },
  { code: "TH09", category: "Human actions",                    text: "Retrieval of recycled or discarded media" },
  { code: "TH10", category: "Human actions",                    text: "Disclosure of information" },
  { code: "TH11", category: "Human actions",                    text: "Data input from untrustworthy sources" },
  { code: "TH12", category: "Human actions",                    text: "Tampering with hardware" },
  { code: "TH13", category: "Human actions",                    text: "Tampering with software" },
  { code: "TH14", category: "Human actions",                    text: "Drive-by-exploits using web-based communication" },
  { code: "TH15", category: "Human actions",                    text: "Replay attack, man-in-the-middle attack" },
  { code: "TH16", category: "Human actions",                    text: "Unauthorized processing of personal data" },
  { code: "TH17", category: "Human actions",                    text: "Unauthorized entry to facilities" },
  { code: "TH18", category: "Human actions",                    text: "Unauthorized use of devices" },
  { code: "TH19", category: "Human actions",                    text: "Incorrect use of devices" },
  { code: "TH20", category: "Human actions",                    text: "Damaging devices or media" },
  { code: "TH21", category: "Human actions",                    text: "Fraudulent copying of software" },
  { code: "TH22", category: "Human actions",                    text: "Use of counterfeit or copied software" },
  { code: "TH23", category: "Human actions",                    text: "Corruption of data" },
  { code: "TH24", category: "Human actions",                    text: "Illegal processing of data" },
  { code: "TH25", category: "Human actions",                    text: "Sending or distributing of malware" },
  { code: "TH26", category: "Human actions",                    text: "Position detection" },
  { code: "TC01", category: "Compromise of functions or services", text: "Error in use" },
  { code: "TC02", category: "Compromise of functions or services", text: "Abuse of rights or permissions" },
  { code: "TC03", category: "Compromise of functions or services", text: "Forging of rights or permissions" },
  { code: "TC04", category: "Compromise of functions or services", text: "Denial of actions" },
  { code: "TO01", category: "Organizational threats",            text: "Lack of staff" },
  { code: "TO02", category: "Organizational threats",            text: "Lack of resources" },
  { code: "TO03", category: "Organizational threats",            text: "Failure of service providers" },
  { code: "TO04", category: "Organizational threats",            text: "Violation of laws or regulations" },
];

export { THREAT_CATALOG };

export const callRiskByAssets = async (assets, currentTitle = "") => {
  try {
    const assetsText = assets
      .map(a => `${a.asset_name || a.name} (${a.category || ""})`)
      .join(", ");

    const { data } = await axios.post(
      `${PYTHON_API_URL}/risk_by_assets`,
      {
        assets: assetsText,
        current_title: currentTitle,
        threat_catalog: THREAT_CATALOG,
        language: getLang(),
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch (err) {
    console.error("Error calling risk_by_assets API:", err);
    throw new Error("Failed to generate risk from assets.");
  }
};

// Non-IT departments: let the AI invent its own threats instead of picking
// from the (IT/security-flavored) THREAT_CATALOG. Subject to change later.
export const callRiskByAssetsFreeform = async (assets, currentTitle = "", departmentName = "", departmentDefinition = "") => {
  try {
    const assetsText = assets
      .map(a => `${a.asset_name || a.name} (${a.category || ""})`)
      .join(", ");

    const { data } = await axios.post(
      `${PYTHON_API_URL}/risk_by_assets_freeform`,
      {
        assets: assetsText,
        current_title: currentTitle,
        department_name: departmentName,
        department_definition: departmentDefinition,
        language: getLang(),
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch (err) {
    console.error("Error calling risk_by_assets_freeform API:", err);
    throw new Error("Failed to generate risk from assets.");
  }
};

export const callOpenAIAPIRisks = async (
  Department_Name,
  Department_Definition,
  Critical_Assets = null
) => {
  try {
    const payload = {
      Department_Name,
      Department_Definition,
    };

    if (Critical_Assets && Critical_Assets.trim() !== "") {
      payload.Critical_Assets = Critical_Assets;
    }

    payload.threat_catalog = THREAT_CATALOG;
    payload.language = getLang();

    const { data } = await axios.post(
      `${PYTHON_API_URL}/department_risks`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );

    return data;
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};



export const callOpenAIAPIFindings = async (Checklist, completed) => {
  try {
    ////console.log("text ", Checklist, "completed ", completed)
    const { data } = await axios.post(
      `${PYTHON_API_URL}/checklist_findings`,
      {
        text: Checklist,
        completed: String(completed).toLowerCase(),
        language: getLang(),
      },
      { headers: { "Content-Type": "application/json" } }
    );

    // `data` itself is the answer
    return data;                         // no `.reply`
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};


export const callOpenAIAPISurveyFindings = async (allClauses, answers) => {
  //console.log("Callign AI")
  //console.log("allClauses ", allClauses)
  //console.log("answers ", answers)
  try {
    const { data } = await axios.post(
      `${PYTHON_API_URL}/survey_findings`,
      {
        allClauses,
        answers,
        language: getLang(),
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    // data = array of findings
    return data;
  } catch (err) {
    console.error("Error calling Survey Findings AI:", err);
    throw new Error("Failed to generate survey findings.");
  }
};

/**
 * AI DISABLED — DEMO MODE
 * These functions mimic the real API but return safe empty values
 */

const delay = (ms = 150) => new Promise((res) => setTimeout(res, ms));

/**
 * Controls suggestion (returns empty)
 */
// const callOpenAIAPI = async (title, description, threats = []) => {
//   console.warn("AI disabled: callOpenAIAPI");
//   await delay();
//   return ""; // empty string like “no suggestion”
// };

// /**
//  * Control checklist (returns empty array)
//  */
// export const callOpenAIAPIChecklist = async (title, description) => {
//   console.warn("AI disabled: callOpenAIAPIChecklist");
//   await delay();
//   return []; // checklist = []
// };

// /**
//  * Department risks (returns empty array)
//  */
// export const callOpenAIAPIRisks = async (Department_Name, Department_Definition) => {
//   console.warn("AI disabled: callOpenAIAPIRisks");
//   await delay();
//   return []; // no risks
// };

// /**
//  * Checklist findings (returns empty array)
//  */
// export const callOpenAIAPIFindings = async (Checklist, completed) => {
//   console.warn("AI disabled: callOpenAIAPIFindings");
//   await delay();
//   return []; // no findings
// };

export default callOpenAIAPI;


